import type { GroupBy } from '../../core/common/types.js';
import {
  type DataSource,
  defaultGroupBy,
  getGroupByService,
  t,
} from '../../core/index.js';

const KANBAN_UNGROUPABLE_PROPERTY_TYPES = new Set(['attachment']);

export function isKanbanL1Groupable(
  dataSource: DataSource,
  columnId: string
): boolean {
  const propertyType = dataSource.propertyTypeGet(columnId);
  if (!propertyType || KANBAN_UNGROUPABLE_PROPERTY_TYPES.has(propertyType)) {
    return false;
  }

  const dataType = dataSource.propertyDataTypeGet(columnId);
  return !!dataType && !!getGroupByService(dataSource)?.matcher.match(dataType);
}

function getKanbanL1Weight(dataSource: DataSource, columnId: string): number {
  const dataType = dataSource.propertyDataTypeGet(columnId);
  if (!dataType || t.string.is(dataType) || t.richText.is(dataType)) {
    return 0;
  }
  if (t.tag.is(dataType)) {
    return 3;
  }
  if (t.array.is(dataType)) {
    return 2;
  }
  return 1;
}

export function getKanbanL1GroupableColumnIds(
  dataSource: DataSource
): string[] {
  return dataSource
    .getBoundedPropertyIdsWindow()
    .propertyIds.filter(columnId => isKanbanL1Groupable(dataSource, columnId))
    .sort(
      (a, b) =>
        getKanbanL1Weight(dataSource, b) - getKanbanL1Weight(dataSource, a)
    );
}

export function createKanbanGroupBy(
  dataSource: DataSource,
  columnId: string
): GroupBy | undefined {
  const dataType = dataSource.propertyDataTypeGet(columnId);
  if (!dataType || !getGroupByService(dataSource)?.matcher.match(dataType)) {
    return;
  }

  const propertyType = dataSource.propertyTypeGet(columnId);
  const propertyMeta = propertyType && dataSource.propertyMetaGet(propertyType);
  const propertyData = dataSource.propertyDataGet(columnId);
  if (!propertyMeta || !propertyData) {
    return;
  }

  return (
    defaultGroupBy(dataSource, propertyMeta, columnId, propertyData) ??
    undefined
  );
}

export function createKanbanL1GroupBy(
  dataSource: DataSource,
  columnId: string
): GroupBy | undefined {
  if (!isKanbanL1Groupable(dataSource, columnId)) {
    return;
  }

  return createKanbanGroupBy(dataSource, columnId);
}

export function createDefaultKanbanL1GroupBy(
  dataSource: DataSource
): GroupBy | undefined {
  const columnId = getKanbanL1GroupableColumnIds(dataSource)[0];
  return columnId ? createKanbanL1GroupBy(dataSource, columnId) : undefined;
}
